<template>
  <v-container data-testid="orders-list-page" class="mt-5">
    <v-card class="pa-4" elevation="1">
      <v-card-title class="text-h5 mb-2">My Orders</v-card-title>

      <!--
        A closed or archived store, and NOT a signed-in shopper (OD-20261016-3,
        "B"): the order-link form and nothing else -- no request is made, and
        nothing about the store is shown (router/ownOrdersSession.js).
      -->
      <div
        v-if="lookupOnly"
        class="d-flex justify-center pa-6"
        data-testid="orders-list-lookup-only"
      >
        <StorefrontGuestOrderLookup />
      </div>

      <div v-else-if="loading" class="d-flex align-center justify-center pa-6">
        <v-progress-circular indeterminate color="primary" class="mr-3" />
        <span>Loading your orders…</span>
      </div>

      <div
        v-else-if="loadError"
        class="text-center pa-6"
        data-testid="orders-list-error"
      >
        <v-icon size="48" color="error" icon="error_outline" class="mb-2" />
        <div class="text-body-1">
          We couldn't load your orders. Please try again later.
        </div>
      </div>

      <div
        v-else-if="orders.length === 0"
        class="text-center pa-6"
        data-testid="orders-list-empty"
      >
        <div class="text-body-1 text-grey-darken-1">
          You haven't placed any orders yet.
        </div>

        <!--
          Guest checkout is the DEFAULT (card tmMqkxM1), so an empty list
          here does NOT mean the shopper has never ordered -- their guest
          orders simply aren't tied to this account. Offer the email-me-my-
          link path rather than leaving them stranded.
        -->
        <div class="d-flex justify-center mt-6">
          <StorefrontGuestOrderLookup />
        </div>
      </div>

      <template v-else>
        <v-table density="comfortable">
          <thead>
            <tr>
              <th class="text-left">Date</th>
              <th class="text-left">Order #</th>
              <th class="text-left">Charged</th>
              <th class="text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="order in pageItems"
              :key="orderId(order)"
              data-testid="orders-list-row"
              class="orders-list-row-clickable"
              @click="goToOrder(orderId(order))"
            >
              <td>{{ formatDate(order.created_at) }}</td>
              <!--
                The STORE's order number, rendered BARE. This printed
                `#{{ orderId(order) }}` -- the internal DB id with a "#" in
                front, which makes it look like a real order number and is
                what a shopper would quote to support. The id still drives
                the `:key` and the row click above; only the DISPLAY changes.
              -->
              <td data-testid="orders-list-order-number">
                {{ orderNumber(order) }}
              </td>
              <!--
                The amount CHARGED, not `order.total`. On a store running
                the free-item rule those differ, and this list previously
                showed the larger (pre-discount) one -- so the shopper's
                order history disagreed with their card statement.
              -->
              <td>{{ formatTotal(chargedAmount(order)) }}</td>
              <!--
                Shopper copy, never the raw enum. This printed
                `awaiting_batch_charge` -- our internal batching vocabulary
                on the screen a shopper reaches FIRST. Same map as the order
                detail page, so the two cannot drift.
              -->
              <td>{{ statusLabel(order) }}</td>
            </tr>
          </tbody>
        </v-table>

        <div
          class="d-flex align-center justify-end mt-4"
          v-if="totalPages > 1"
        >
          <v-btn
            variant="outlined"
            size="small"
            class="mr-2"
            :disabled="page <= 1"
            data-testid="orders-list-prev"
            @click="goPrev"
          >
            Previous
          </v-btn>
          <span
            class="mx-3"
            data-testid="orders-list-page-indicator"
          >Page {{ page }} of {{ totalPages }}</span>
          <v-btn
            variant="outlined"
            size="small"
            class="ml-2"
            :disabled="page >= totalPages"
            data-testid="orders-list-next"
            @click="goNext"
          >
            Next
          </v-btn>
        </div>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import {
  formatOrderDate,
  formatOrderTotal,
  getOrderChargedAmount,
  getOrderId,
  getOrderNumber,
  paginateOrders,
  sortOrdersNewestFirst,
} from "./orderListHelpers";
// Shared with StorefrontOrderDetail -- one status map, both shopper surfaces.
import { shopperOrderStatus } from "./orderStatusCopy";
// PR-R5: storefront URL restructure — `/orders/:id` -> `/store/:slug/orders/:id`.
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import StorefrontGuestOrderLookup from "./StorefrontGuestOrderLookup.vue";
import {
  STOREFRONT_NO_STORE_SESSION,
  ownOrdersPageMode,
  personalShopperAccountId,
} from "@/router/ownOrdersSession";

const DEFAULT_PAGE_SIZE = 25;

export default {
  name: "StorefrontOrdersList",
  components: { StorefrontGuestOrderLookup },
  inject: {
    // True when the session gate rendered this page with no store session
    // (a closed or archived store; router/ownOrdersSession.js).
    noStoreSession: { from: STOREFRONT_NO_STORE_SESSION, default: false },
  },
  data() {
    return {
      lookupOnly: false,
      orders: [],
      page: 1,
      perPage: DEFAULT_PAGE_SIZE,
      loading: false,
      loadError: false,
    };
  },
  computed: {
    sortedOrders() {
      return sortOrdersNewestFirst(this.orders);
    },
    paginated() {
      return paginateOrders(this.sortedOrders, this.page, this.perPage);
    },
    pageItems() {
      return this.paginated.items;
    },
    totalPages() {
      return this.paginated.totalPages;
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    formatDate(iso) {
      return formatOrderDate(iso);
    },
    formatTotal(amount) {
      return formatOrderTotal(amount);
    },
    orderId(order) {
      return getOrderId(order);
    },
    orderNumber(order) {
      return getOrderNumber(order);
    },
    chargedAmount(order) {
      return getOrderChargedAmount(order);
    },
    statusLabel(order) {
      return shopperOrderStatus(order?.status, order?.payment_state);
    },
    goPrev() {
      if (this.page > 1) this.page -= 1;
    },
    goNext() {
      if (this.page < this.totalPages) this.page += 1;
    },
    goToOrder(id) {
      if (id == null) return;
      // Convergence (card q7EQ3a0b): order detail moved to
      // /shop/:slug/orders/:id.
      const slug = resolveStorefrontSlug(this.$route);
      const dest = slug ? `/shop/${slug}/orders/${id}` : `/orders/${id}`;
      this.$router.push(dest);
    },
    async load() {
      const personalAccountId = personalShopperAccountId();
      const mode = ownOrdersPageMode({
        noStoreSession: this.noStoreSession,
        personalAccountId,
      });
      if (mode === "lookup") {
        this.lookupOnly = true;
        return;
      }
      // A signed-in shopper of a closed or archived store: the read is this
      // page's to recover (an expired session lands on the order-link form,
      // not on a logout to a store home that no longer opens).
      // A closed or archived store: no store row is requested (the page never
      // reads it, and that store's row is not the shopper's to receive).
      const config = {
        params: {
          include: mode === "own-orders" ? "account" : "account,campaign",
        },
      };
      if (mode === "own-orders") config.skipAuthRecovery = true;
      this.loading = true;
      this.loadError = false;
      try {
        const result = await this.$store.dispatch("jv/get", ["orders", config]);
        // jsonapi-vuex returns an object keyed by id; flatten to an array.
        const fetched = Object.values(result || {});
        // Defense in depth (CLAUDE.md Principle 5 + ADR-0001).
        // Backend /api/orders/ scopes by account_id via Shopper role policy
        // (api/src/models/mixins/roles.py — fix landed in PR #964 / issue #963).
        // We additionally filter client-side so a future backend regression
        // can't silently leak other shoppers' orders through this page.
        const myAccountId =
          this.$store.state.account?._jv?.id ??
          (mode === "own-orders" ? personalAccountId : null);
        this.orders = myAccountId
          ? fetched.filter(
              (o) =>
                String(o?.account?._jv?.id ?? o?.account_id ?? "") ===
                String(myAccountId),
            )
          : fetched;
      } catch (err) {
        if (mode === "own-orders") {
          this.lookupOnly = true;
          return;
        }
        this.loadError = true;
        // eslint-disable-next-line no-console
        console.error("Failed to load past orders", describeError(err));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.orders-list-row-clickable {
  cursor: pointer;
}
</style>
